import {
  forwardRef,
  inject,
  Injectable,
  InputSignal,
  Type,
} from '@angular/core';
import {
  distinctUntilChanged,
  NEVER,
  Observable,
  shareReplay,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

export type ModalResolver<Result = void> = {
  resolve: (result?: Result) => void;
};

export type ModalComponent<Context, Result = void> = {
  context: InputSignal<Context>;
  resolver: InputSignal<ModalResolver<Result>>;
};

export interface OpenModalEvent<T, U> {
  component: Type<ModalComponent<T, U>>;
  context: T;
  callback: (result: U | undefined) => void;
}

export const INVALID_MODAL_CONTEXT = Symbol('INVALID_MODAL_CONTEXT');

export type ModalContextFactory<Context> = (
  queryParamValue: string,
) => Context | typeof INVALID_MODAL_CONTEXT;

@Injectable({
  providedIn: 'root',
  useExisting: forwardRef(() => ModalServiceImpl),
})
export abstract class ModalService {
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);

  abstract showModal<T, U>(
    component: Type<ModalComponent<T, U>>,
    context: T,
  ): Observable<U | undefined>;

  createRoutableModal<Context, Result>(
    component: Type<ModalComponent<Context, Result>>,
    queryParamName: string,
    context: Context extends undefined
      ? undefined
      : ModalContextFactory<Context> | Context | typeof INVALID_MODAL_CONTEXT,
  ): Observable<Result | undefined> {
    return this.#route.queryParams.pipe(
      distinctUntilChanged(),
      switchMap((q) => {
        const paramValue = q[queryParamName];
        if (!paramValue || paramValue === 'false') {
          return NEVER;
        }
        const contextValue =
          typeof context === 'function' ? context(q[queryParamName]) : context;
        if (contextValue === INVALID_MODAL_CONTEXT) {
          return NEVER;
        }
        return this.showModal(component, contextValue);
      }),
      tap(() => {
        this.#router
          .navigate([], {
            queryParams: {
              [queryParamName]: null,
            },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          })
          .catch(console.error);
      }),
    );
  }
}

@Injectable({
  providedIn: 'root',
})
export class ModalServiceImpl extends ModalService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly #renderModal$ = new Subject<OpenModalEvent<any, any>>();
  readonly renderModal$ = this.#renderModal$.asObservable();

  readonly #dismissModal$ = new Subject<void>();
  readonly dismissModal$ = this.#dismissModal$.asObservable();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  #lastCloseCallback?: (result?: any) => void;

  showModal<T, U>(
    component: Type<ModalComponent<T, U>>,
    context: T,
  ): Observable<U | undefined> {
    return new Observable<U | undefined>((subscriber) => {
      const callback = (result?: U) => {
        if (callback === this.#lastCloseCallback) {
          this.#lastCloseCallback = undefined;
          subscriber.next(result);
          subscriber.complete();
        } else {
          throw new Error(`Attempt to close a modal that's no longer active`);
        }
      };

      this.#lastCloseCallback = callback;

      const event: OpenModalEvent<T, U> = {
        component,
        context,
        callback,
      };

      this.#renderModal$.next(event);

      return () => {
        this.#dismissModal$.next();
      };
    }).pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }
}
